/* eslint-disable no-nested-ternary */
import React, { ReactNode, useState } from 'react'
import _ from 'lodash'
import { useDispatch } from 'react-redux'
import { PokerGameModel } from 'mvppoker-portal-common/api'
import MathUtils from 'mvppoker-portal-common/utils/MathUtils'
import { useMediaQuery } from '@mui/material'
import { useAvailableRooms } from '../../state/hooks/roomsHooks'
import { makeActiveGame } from '../../state/reducers/activeGame'
import { MVPButton } from '../../Views/Buttons/MVPButton'
import { useNavigate } from '../navigation'
import { useActiveUserId } from '../../state/hooks/ActiveUserHooks'
import { FilteringBox, RoomListOptions } from './FilteringBox/FilteringBox'
import { RoomTable } from './RoomsTable/RoomsTable'
import VerticalRoomsTable from './RoomsTable/Vertical/VerticalRoomsTable'
import { useHoursOperationsAlert } from './useHoursOperationsAlert'
import Marquee from './Marquee/Marquee'
import { LobbyChatView } from './Chat/LobbyChatView'
import '../../index.scss'
import './LobbyPage.scss'

const stakesComparator = (asc: boolean) => {
	return (game: PokerGameModel): number => parseFloat(game.config.bigBlindAmount) * (asc ? 1 : -1)
}
const avgPotComparator = (asc: boolean) => {
	return (game: PokerGameModel): number =>
		parseFloat(MathUtils.DivideNumbers(game.statistics.totalPotAmount, `${game.statistics.gamesPlayed}`)) * (asc ? 1 : -1)
}
const playersComparator = (asc: boolean) => {
	return (game: PokerGameModel): number => game.players.length * (asc ? 1 : -1)
}

type LayoutProps = {
	roomsTable: ReactNode
	enterRoomButton: ReactNode
	filteringBox: ReactNode
}

const LargeLayout = ({ roomsTable, enterRoomButton, filteringBox }: LayoutProps) => {
	return (
		<>
			<h1>LIVE DEALT POKER | LOBBIES</h1>
			<div id='center-box' className='highlight-box'>
				<div id='l' className='-no-scrollbar'>
					{roomsTable}
				</div>
				<div id='r'>
					{/* <InfoBox /> */}
					{filteringBox}
					{enterRoomButton}
				</div>
			</div>
		</>
	)
}

const VerticalLayout = ({ roomsTable, enterRoomButton, filteringBox }: LayoutProps) => {
	return (
		<>
			<h1>LIVE DEALT POKER | LOBBIES</h1>
			<div id='vertical-center-box'>
				{filteringBox}
				{roomsTable}
				{enterRoomButton}
			</div>
		</>
	)
}

export default () =>
{
	const activeUserId = useActiveUserId()
	const rooms = useAvailableRooms()
	const gotoPrepareGameVideo = useNavigate('prepareVideo')
	const [selectedRoom, setSelectedRoom] = useState<string | undefined>()
	const dispatch = useDispatch()
	const [joining, setJoining] = useState(false)
	const [options, setOptions] = useState<RoomListOptions>({ filteringOption: 'players', filteringOrder: 'desc', game: 'All', format: 'CASH' })
	const isSmallScreen = useMediaQuery('(max-width: 650px)')

	useHoursOperationsAlert()

	const debug = false

	const sortedRooms = _(rooms)
		.filter((it) => (it.config.format === options.format))
		.filter((it) => (options.game === 'All' ? true : it.config.type === options.game))
		.filter((it) => debug || !it.status.hidden)
		.sortBy(
			options.filteringOption === 'stakes'
				? [stakesComparator(options.filteringOrder === 'asc')]
				: options.filteringOption === 'avg-pot'
				? [avgPotComparator(options.filteringOrder === 'asc')]
				: options.filteringOption === 'players'
				? [playersComparator(options.filteringOrder === 'asc')]
				: _.identity,
		)
		.value()

	const handleFilterFormatToggle = (option: RoomListOptions['format']) => {
		setOptions((old) => {
			return { ...old, format: option }
		})
	}
	const handleFilterGameToggle = (option: RoomListOptions['game']) => {
		setOptions((old) => {
			return { ...old, game: option }
		})
	}
	const handleFilterOptionToggle = (option: RoomListOptions['filteringOption']) => {
		setOptions((old) => {
			if (old.filteringOption === option) {
				return { ...old, filteringOrder: old.filteringOrder === 'asc' ? 'desc' : 'asc' }
			}
			return { ...old, filteringOption: option }
		})
	}

	// AppBar does it
	
	// useEffect(() => {
	// 	const refreshTask = () => {
	// 		updateService.refreshRooms()
	// 	}

	// 	refreshTask()
	// 	const id = setInterval(refreshTask, ROOMS_REFRESH_RATE)

	// 	return () => {
	// 		clearInterval(id)
	// 	}
	// }, [])

	const handleEnterRoom = () => {
		if (selectedRoom) {
			const room = rooms.find((it) => it.id === selectedRoom)!

			setJoining(true)
			
			dispatch(makeActiveGame({ ...room, userId: activeUserId! }))
			gotoPrepareGameVideo({ replace: true })
		}
	}

	const handleClickRoom = (id: string) => {
		if (id === selectedRoom) {
			handleEnterRoom()
		} else {
			setSelectedRoom(id)
		}
	}

	const Layout = isSmallScreen ? VerticalLayout : LargeLayout
	const Table = isSmallScreen ? VerticalRoomsTable : RoomTable

	const isSmallClassName = isSmallScreen ? 'is-small' : ''

	return (
		<div id='lobby-page'>
			<div id='main' className={isSmallClassName}>
				<Layout
					roomsTable={<Table format={options.format} rooms={sortedRooms} selectedRoom={selectedRoom} onSelectRoom={handleClickRoom} />}
					enterRoomButton={
						<MVPButton
							id='enter-room-button'
							className='semiround small'
							onClick={handleEnterRoom}
							disabled={selectedRoom === undefined}
							loading={joining}
						>
							Enter Room
						</MVPButton>
					}
					filteringBox={<FilteringBox options={options} onClickFormat={handleFilterFormatToggle} onClickGame={handleFilterGameToggle} onClickOption={handleFilterOptionToggle} />}
				/>
			</div>
			<Marquee />
			<LobbyChatView />
		</div>
	)
}
